import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Avatar, List, ListItemButton, ListItemAvatar, ListItemText, Badge, Divider, Paper, TextField, Button, CircularProgress, IconButton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ChevronLeft, DeleteOutline, ImageOutlined, Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiGetThreads, apiGetMessages, apiSendMessage, apiMarkRead, apiGetUserById, apiDeleteMessage } from '../../services/api';
import { connectIfAuth, getSocket } from '../../services/socket';
import { uploadImage, IMAGE_ACCEPT_ATTR, isSafeHttpUrl } from '../../utils/imageUpload';
import { toThread, toMessage } from '../../api/messages';
import type { Thread, DmMessage } from '../../types';
import type { AnyDate } from '../../utils/formatTimeAgo';
import { toDate } from '../../utils/formatTimeAgo';

const SUPER_ADMIN_EMAIL = 'hl865@drexel.edu';
const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';

function formatMessageTime(input: AnyDate): string {
  const date = toDate(input);
  if (!date) return '';

  const diffMs = Math.max(0, Date.now() - date.getTime());
  const minutes = Math.max(1, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
}

function formatDaySeparator(input: AnyDate): string {
  const date = toDate(input);
  if (!date) return '';

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((todayStart.getTime() - dateStart.getTime()) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function isSameDay(a: AnyDate, b: AnyDate): boolean {
  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) return false;
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

const Inbox: React.FC = () => {
  const { peerId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isSuperAdmin = String(me?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [items, setItems] = useState<DmMessage[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteMessageId, setConfirmDeleteMessageId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [attachUrl, setAttachUrl] = useState<string | null>(null);
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width:899px)');

  const [activePeer, setActivePeer] = useState<{ id: string; displayName: string; profileImage?: string | null } | null>(null);

  const oldestUnreadIndex = useMemo(() => {
    if (!me) return -1;
    let idx = -1;
    for (let i = 0; i < items.length; i += 1) {
      const m = items[i];
      if (m.receiverId === me.id && !m.isRead) idx = i;
      else if (idx !== -1) break;
    }
    return idx;
  }, [items, me]);

  function canDeleteMessage(message: DmMessage) {
    if (isSuperAdmin) return true;
    if (!me || message.senderId !== me.id) return false;
    const elapsed = Date.now() - new Date(message.createdAt).getTime();
    return elapsed <= 15 * 60 * 1000;
  }

  async function refreshThreads() {
    try {
      const raw = await apiGetThreads();
      setThreads((raw as any[]).map(toThread));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load threads');
      setThreads([]);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const raw = await apiGetThreads();
        if (!alive) return;
        setThreads((raw as any[]).map(toThread));
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message ?? 'Failed to load threads');
        setThreads([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  async function loadInitial(pid: string) {
    setLoading(true);
    try {
      const data: any = await apiGetMessages(pid);
      setItems((data.items as any[]).map(toMessage));
      setNextCursor((data as any).nextCursor ?? null);
      apiMarkRead(pid).catch(()=>{});
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }), 0);
      setThreads(prev => prev?.map(t => t.peer.id === pid ? { ...t, unreadCount: 0 } : t) ?? null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!peerId) return;
    setItems([]);
    setNextCursor(null);
    loadInitial(peerId);
  }, [peerId]);

  useEffect(() => {
    if (!me) return;
    connectIfAuth(me);
    const socket = getSocket();
    if (!socket) return;

    const syncActiveThread = () => {
      socket.emit('chat_open', { peerId: peerId ?? null });
    };

    const handleIncomingMessage = (raw: any) => {
      const incoming = toMessage(raw);
      if (!me) return;

      // Ignore our own messages for unread counters/notifications.
      if (incoming.senderId === me.id) return;

      const incomingPeerId = incoming.senderId;
      const isActiveThread = peerId === incomingPeerId;
      const isVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;

      if (isActiveThread && isVisible) {
        setItems((prev) => (prev.some((m) => m.id === incoming.id) ? prev : [incoming, ...prev]));
        setThreads((prev) => prev?.map((t) => (
          t.peer.id !== incomingPeerId
            ? t
            : { ...t, lastMessage: incoming as any, unreadCount: 0 }
        )) ?? prev ?? null);

        apiMarkRead(incomingPeerId).catch(() => {});
        return;
      }

      setThreads((prev) => {
        if (!prev) return prev;
        const index = prev.findIndex((t) => t.peer.id === incomingPeerId);
        const fallbackName = raw?.sender?.displayName || raw?.sender?.firstName || 'Unknown Student';
        const fallbackImage = raw?.sender?.profileImage || null;

        if (index === -1) {
          return [{
            peer: { id: incomingPeerId, displayName: fallbackName, profileImage: fallbackImage },
            lastMessage: incoming as any,
            unreadCount: 1,
          }, ...prev];
        }

        const current = prev[index];
        const updated = {
          ...current,
          lastMessage: incoming as any,
          unreadCount: current.unreadCount + 1,
        };
        return [updated, ...prev.slice(0, index), ...prev.slice(index + 1)];
      });
    };

    socket.on('new_message', handleIncomingMessage);
    socket.on('connect', syncActiveThread);
    if (socket.connected) syncActiveThread();

    return () => {
      socket.off('new_message', handleIncomingMessage);
      socket.off('connect', syncActiveThread);
      socket.emit('chat_close');
    };
  }, [me, peerId]);

  // Keep active peer header in sync and create a virtual thread if needed
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!peerId) { setActivePeer(null); return; }

      // try find in existing threads first
      const fromThreads = (threads ?? []).find(t => t.peer.id === peerId)?.peer ?? null;
      if (fromThreads) { setActivePeer(fromThreads); return; }

      // fallback to fetch user meta
      try {
        const resp: any = await apiGetUserById(peerId);
        const u = resp?.data ?? resp;
        const meta = { id: u.id, displayName: u.displayName ?? 'Unknown Student', profileImage: u.profileImage ?? null };
        if (!cancelled) setActivePeer(meta);
        // insert a virtual thread so left pane shows something
        setThreads(prev => {
          if (!prev) return prev; // wait until initial load finishes
          const exists = prev.some(t => t.peer.id === meta.id);
          if (exists) return prev;
          const virtual = {
            peer: { ...meta },
            lastMessage: { id: 'virtual', content: '', senderId: '', receiverId: '', isRead: true, createdAt: new Date() },
            unreadCount: 0,
          } as Thread;
          return [virtual, ...prev];
        });
      } catch {
        if (!cancelled) setActivePeer({ id: peerId, displayName: 'Unknown Student', profileImage: null });
      }
    })();
    return () => { cancelled = true; };
  }, [peerId, threads]);

  async function loadMore() {
    if (!peerId || !nextCursor || loading) return;
    setLoading(true);
    try {
      const data: any = await apiGetMessages(peerId, nextCursor);
      setItems(prev => [...prev, ...(data.items as any[]).map(toMessage)]);
      setNextCursor((data as any).nextCursor ?? null);
    } finally {
      setLoading(false);
    }
  }

  async function handleAttachFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAttachError(null);
    setAttachUploading(true);
    try {
      const url = await uploadImage(file);
      setAttachUrl(url);
    } catch (err: any) {
      setAttachError(err?.message ?? 'Upload failed. Please try again.');
    } finally {
      setAttachUploading(false);
    }
  }

  async function send() {
    const content = text.trim();
    const image = attachUrl;
    if ((!content && !image) || sending || attachUploading || !peerId || !me) return;
    setSending(true);

    const tempId = 'temp-' + Math.random().toString(36).slice(2);
    const optimistic: DmMessage = {
      id: tempId,
      content,
      imageUrl: image,
      senderId: me.id,
      receiverId: peerId,
      isRead: true,
      createdAt: new Date(),
    };

    setItems(prev => [optimistic, ...prev]);
    setText('');
    setAttachUrl(null);
    setAttachError(null);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);

    try {
      const saved = toMessage(await apiSendMessage(peerId, content, image) as unknown as any);
      setItems(prev => prev.map(m => m.id === tempId ? saved : m));
      setThreads(prev => prev?.map(t =>
        t.peer.id !== peerId ? t : { ...t, lastMessage: saved as any, unreadCount: 0 }
      ) ?? null);
    } catch (e) {
      setItems(prev => prev.filter(m => m.id !== tempId));
      setText(content);
      setAttachUrl(image);
    } finally {
      setSending(false);
    }
  }

  // detect scroll top to load more
  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    if (e.currentTarget.scrollTop < 32) {
      loadMore();
    }
  }

  async function removeMessage(messageId: string) {
    if (!peerId || deletingId) return;
    const previousItems = items;
    setDeletingId(messageId);
    setItems(prev => prev.filter(m => m.id !== messageId));
    try {
      await apiDeleteMessage(messageId);
      await refreshThreads();
    } catch (e) {
      setItems(previousItems);
    } finally {
      setConfirmDeleteMessageId(null);
      setDeletingId(null);
    }
  }

  function openActivePeerProfile() {
    if (!activePeer?.id) return;
    const target = me?.id && activePeer.id === me.id ? '/profile' : `/profile/${activePeer.id}`;
    navigate(target);
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
        gap: 2,
        alignItems: 'stretch',
      }}
    >
      {/* Threads list */}
      <Paper
        variant="outlined"
        sx={{
          height: 'calc(100vh - 160px)',
          overflow: 'auto',
          display: { xs: peerId ? 'none' : 'block', md: 'block' },
          borderRadius: 3,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: 2.25, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.1 }}>
            Messages
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Your conversations with fellow students.
          </Typography>
        </Box>
        {threads === null && <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>}
        {threads && threads.length === 0 && <Box sx={{ p: 2 }}><Typography variant="body2">No conversations yet.</Typography></Box>}
        <List disablePadding>
          {(threads ?? []).map((t) => {
            const hasUnread = t.unreadCount > 0;
            return (
              <ListItemButton
                key={t.peer.id}
                selected={peerId === t.peer.id}
                onClick={() => navigate(`/messages/${t.peer.id}`)}
                sx={{
                  px: 2,
                  py: 1.2,
                  alignItems: 'center',
                  borderLeft: '3px solid transparent',
                  borderLeftColor: hasUnread ? 'primary.main' : 'transparent',
                  backgroundColor: hasUnread ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    borderLeftColor: 'primary.main',
                    backgroundColor: 'action.selected',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <ListItemAvatar>
                  <Badge color="primary" overlap="circular" badgeContent={hasUnread ? t.unreadCount : null}>
                    <Avatar
                      src={t.peer.profileImage || undefined}
                      sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700 }}
                    >
                      {(t.peer.displayName || '?').slice(0,1)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={t.peer.displayName}
                  primaryTypographyProps={{ fontWeight: hasUnread ? 800 : 600, noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true, fontWeight: hasUnread ? 600 : 400, color: hasUnread ? 'text.primary' : 'text.secondary' }}
                  secondary={t.lastMessage.content
                    ? `${t.lastMessage.content.slice(0, 60)}${t.lastMessage.content.length > 60 ? '…' : ''}`
                    : (t.lastMessage.imageUrl ? '📷 Photo' : '')}
                />
                <Typography variant="caption" sx={{ ml: 1, color: hasUnread ? 'primary.main' : 'text.secondary', fontWeight: hasUnread ? 700 : 600 }}>
                  {formatMessageTime(t.lastMessage.createdAt)}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Paper>

      {/* Conversation */}
      <Paper
        variant="outlined"
        sx={{
          height: 'calc(100vh - 160px)',
          display: { xs: peerId ? 'flex' : 'none', md: 'flex' },
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
          borderColor: 'divider',
        }}
      >
        {!peerId ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6">Select a conversation</Typography>
            <Typography variant="body2" color="text.secondary">Choose a thread to start chatting.</Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                px: 2,
                py: 1.6,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'background.paper',
              }}
            >
              {isMobile && (
                <IconButton onClick={() => navigate('/messages')} size="small" sx={{ mr: 0.5 }}>
                  <ChevronLeft />
                </IconButton>
              )}
              <Box
                onClick={openActivePeerProfile}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openActivePeerProfile();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: activePeer?.id ? 'pointer' : 'default',
                  borderRadius: 2,
                  px: 0.5,
                  py: 0.25,
                  '&:hover': {
                    backgroundColor: activePeer?.id ? 'action.hover' : 'transparent',
                  },
                }}
              >
                <Avatar
                  src={activePeer?.profileImage ?? undefined}
                  sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700 }}
                >
                  {(activePeer?.displayName?.[0] || '?').toUpperCase()}
                </Avatar>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {activePeer?.displayName ?? 'Conversation'}
                </Typography>
              </Box>
            </Box>
            <Box
              ref={listRef}
              onScroll={onScroll}
              sx={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column-reverse',
                p: 2,
                gap: 1.5,
                bgcolor: 'background.default',
              }}
            >
              <div ref={bottomRef} />
              {items.map((m, idx) => (
                <React.Fragment key={m.id}>
                  <Box sx={{ display: 'flex', justifyContent: m.senderId === me?.id ? 'flex-end' : 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.55, maxWidth: { xs: '92%', sm: '78%' } }}>
                      {m.senderId === me?.id && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            lineHeight: 1,
                            mb: 0.25,
                            minWidth: '2.25rem',
                            textAlign: 'center',
                          }}
                        >
                          {formatMessageTime(m.createdAt)}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: 3,
                          maxWidth: '100%',
                          border: m.senderId === me?.id ? 'none' : '1px solid',
                          borderColor: m.senderId === me?.id ? 'transparent' : 'divider',
                          background: m.senderId === me?.id
                            ? (theme) => `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                            : 'background.paper',
                          color: m.senderId === me?.id ? 'primary.contrastText' : 'text.primary',
                        }}
                      >
                        {m.imageUrl && isSafeHttpUrl(m.imageUrl) && (
                          <Box component="a" href={m.imageUrl} target="_blank" rel="noopener noreferrer" aria-label="Open image in new tab" sx={{ display: 'block', mb: m.content ? 0.75 : 0 }}>
                            {/* Fixed-size frame so lazily-decoded images don't grow bubbles
                                and jump the conversation while scrolling history. */}
                            <Box sx={{ width: 220, maxWidth: '100%', aspectRatio: '4 / 3', overflow: 'hidden', borderRadius: 2 }}>
                              <Box
                                component="img"
                                src={m.imageUrl}
                                alt="Message image"
                                loading="lazy"
                                sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                              />
                            </Box>
                          </Box>
                        )}
                        {m.content && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, wordBreak: 'break-word' }}>
                            {m.content}
                          </Typography>
                        )}
                      </Box>
                      {m.senderId !== me?.id && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            lineHeight: 1,
                            mb: 0.25,
                            minWidth: '2.25rem',
                            textAlign: 'center',
                          }}
                        >
                          {formatMessageTime(m.createdAt)}
                        </Typography>
                      )}
                      {canDeleteMessage(m) && !String(m.id).startsWith('temp-') && (
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDeleteMessageId(m.id)}
                          disabled={deletingId === m.id}
                          sx={{ opacity: 0.7 }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {idx === oldestUnreadIndex && (
                    <Box sx={{ py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.2,
                            borderRadius: 99,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            color: 'text.secondary',
                            fontWeight: 700,
                            letterSpacing: 0.2,
                          }}
                        >
                          New messages
                        </Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                      </Box>
                    </Box>
                  )}

                  {idx < items.length - 1 && !isSameDay(m.createdAt, items[idx + 1]?.createdAt) && (
                    <Box sx={{ py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1.1,
                            py: 0.15,
                            borderRadius: 99,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            color: 'text.secondary',
                            fontWeight: 700,
                            letterSpacing: 0.15,
                          }}
                        >
                          {formatDaySeparator(m.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </React.Fragment>
              ))}
              {items.length === 0 && !loading && activePeer && (
                <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                  Start the conversation with {activePeer.displayName}.
                </Box>
              )}
              {loading && <Typography variant="caption" color="text.secondary">Loading…</Typography>}
            </Box>
            <Divider />
            <Box sx={{ p: 1.4, bgcolor: 'background.paper' }}>
              {(attachUrl || attachUploading || attachError) && (
                <Box sx={{ mb: 1 }}>
                  {attachUploading && (
                    <Typography variant="caption" color="text.secondary">Uploading image…</Typography>
                  )}
                  {attachUrl && !attachUploading && (
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Box
                        component="img"
                        src={attachUrl}
                        alt="Attachment preview"
                        sx={{ maxHeight: 120, maxWidth: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'block' }}
                      />
                      <IconButton
                        size="small"
                        aria-label="Remove image"
                        onClick={() => { setAttachUrl(null); setAttachError(null); }}
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {attachError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>{attachError}</Typography>
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <input ref={fileInputRef} hidden type="file" accept={IMAGE_ACCEPT_ATTR} onChange={handleAttachFile} />
                <IconButton
                  aria-label="Attach image"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachUploading || sending}
                  sx={{ mb: 0.25 }}
                >
                  <ImageOutlined />
                </IconButton>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={5}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'action.hover',
                    },
                  }}
                  placeholder={activePeer ? `Message ${activePeer.displayName}…` : 'Type a message…'}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                />
                <Button
                  variant="contained"
                  disabled={sending || attachUploading || (!text.trim() && !attachUrl)}
                  onClick={send}
                  sx={{ borderRadius: 999, px: 2.2, fontWeight: 700 }}
                >
                  Send
                </Button>
              </Box>
            </Box>

            {confirmDeleteMessageId && (
              <Paper elevation={6} sx={{ position: 'fixed', bottom: 24, right: 24, p: 2, zIndex: 1400 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Are you sure you want to remove this message?</Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={() => setConfirmDeleteMessageId(null)}
                    disabled={!!deletingId}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    disabled={!!deletingId}
                    onClick={() => removeMessage(confirmDeleteMessageId)}
                  >
                    {deletingId ? 'Removing…' : 'Yes, remove'}
                  </Button>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Inbox;
